/**
@format
@flow
*/

import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import ActionTypes from '@ziynx/action-types';
import { Button } from '@ziynx/components/buttons';
import { Heading, Body } from '@ziynx/components/typography';
import { withAppManager, getMeasures } from '@ziynx/components/utilities';
import Modal from '@ziynx/components/modals';
import Colors from '@ziynx/theme/colors';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

type Props = $ReadOnly<{|
  /**
   * Signal to open modal
   */
  open?: boolean,
  /**
   * Member object to pass in
   */
  removeMemberConfig: {
    member?: string,
    memberId?: string,
    userStatus?: 'CONFIRMED' | 'EXPIRED' | 'PENDING',
  },
  /**
   * Config object for button group of
   * 1 button destructive and 1 button primary
   */
  buttonGroup?: {
    destructive: {
      children: any,
      onClick?: () => any,
    },
    primary: {
      children: any,
      onClick?: () => any,
    }
  },
  /**
   * Function to close modal from team
   */
  onClose: () => void,

|}>;

type DispatchType = {
  type: string,
} => void;

const MODAL = {
  height: {
    mobileSmall: '294px',
    mobile: '270px',
    tablet: '270px',
    default: '208px',
  },
  width: {
    mobileSmall: '288px',
    mobile: '381px',
    tablet: '381px',
    default: '520px',
  },
};

const BUTTON_GROUP_WIDTH = {
  mobileSmall: '100%',
  mobile: '100%',
  tablet: '100%',
  default: '472px',
};

const BUTTON_SINGLE_WIDTH = {
  mobileSmall: '100%',
  mobile: '100%',
  tablet: '100%',
  default: '228px',
};

const StyledModal = styled(Modal)`
  padding: 24px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  position: relative;
  text-align: center;
`;

const HeadingWrapper = styled.span`
  padding-bottom: 12px;
  height: 72px;
`;

const StyledHeading = styled(Heading)`
  display: -webkit-box; 
  -webkit-line-clamp: 3; 
  -webkit-box-orient: vertical; 
  text-overflow: ellipsis;
  overflow: hidden;
  max-height: 72px;
`;

const BodyWrapper = styled.span`
  padding-bottom: 32px;
  width: 100%;
`;

const StyledBody = styled(Body)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const ButtonGroupSection = styled.div`
  display: flex;
  flex-direction: ${({ isNotDesktop }) => (isNotDesktop ? 'column' : 'row')};
  width: ${({ viewport }) => getMeasures(BUTTON_GROUP_WIDTH, viewport)};
`;

const StyledButton = styled(Button)`
  ${({ lastChild, isNotDesktop }) => !lastChild && isNotDesktop && 'margin-bottom: 16px;'};
  ${({ marginRight, isNotDesktop }) => marginRight && !isNotDesktop && 'margin-right: 16px;'};
`;

const RemoveModal = (props: ManagedProps<Props>): React$Node => {
  const {
    open, buttonGroup, onClose, removeMemberConfig, viewport,
  } = props;

  const dispatch: DispatchType = useDispatch();

  const isNotDesktop = viewport !== 'desktop' && viewport !== 'desktopSmall';

  const MODAL_HEIGHT = getMeasures(MODAL?.height, viewport);

  const MODAL_WIDTH = getMeasures(MODAL?.width, viewport);

  const BUTTON_WIDTH = getMeasures(BUTTON_SINGLE_WIDTH, viewport);

  const _onClickDestructive = (): void => {
    const { onClick } = buttonGroup?.destructive || {};

    switch (removeMemberConfig?.userStatus) {
    case 'PENDING':
    case 'EXPIRED':
      dispatch({
        type: ActionTypes.teamMember.removeInvite.START,
        token: removeMemberConfig?.memberId,
      });
      break;
    case 'CONFIRMED':
    default:
      dispatch({
        type: ActionTypes.team.removeUser.START,
        memberId: removeMemberConfig?.memberId,
      });
      break;
    }

    if (onClick) onClick();
    if (onClose) onClose();
  };

  const _onClickPrimary = (): void => {
    const { onClick } = buttonGroup?.primary || {};
    if (onClick) onClick();
    if (onClose) onClose();
  };

  return (
    <StyledModal
      open={open}
      width={MODAL_WIDTH}
      height={MODAL_HEIGHT}
    >
      <ModalContent>
        <HeadingWrapper>
          <StyledHeading small color={Colors.GrayVeryDark}>
            Are you sure you want to remove
            {' '}
            {removeMemberConfig?.member}
            {' '}
            ?
          </StyledHeading>
        </HeadingWrapper>
        <BodyWrapper>
          <StyledBody medium color={Colors.GrayDark}>
            This action cannot be undone.
          </StyledBody>
        </BodyWrapper>
        <ButtonGroupSection isNotDesktop={isNotDesktop} viewport={viewport}>
          <StyledButton
            variant="destructive"
            isNotDesktop={isNotDesktop}
            marginRight
            width={BUTTON_WIDTH}
            onClick={_onClickDestructive}
          >
            {buttonGroup?.destructive?.children}
          </StyledButton>
          <StyledButton
            variant="primary"
            lastChild
            inverted
            isNotDesktop={isNotDesktop}
            width={BUTTON_WIDTH}
            onClick={_onClickPrimary}
          >
            {buttonGroup?.primary?.children}
          </StyledButton>
        </ButtonGroupSection>

      </ModalContent>
    </StyledModal>
  );
};

export default (withAppManager(RemoveModal): React$ComponentType<ManagedProps<Props>>);
